if (!customElements.get('editorial-hero-stack')) {
  customElements.define(
    'editorial-hero-stack',
    class EditorialHeroStack extends HTMLElement {
      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('.eh-card'));
        this.order = this.cards.map((_, index) => index);
        this.isSpread = false;
        this.inView = true;
        this.render();

        if (this.cards.length < 2) return;

        this.delay = (parseFloat(this.dataset.interval) || 4) * 1000;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

        this.addEventListener('mouseenter', () => {
          if (this.finePointer.matches) this.setSpread(true);
        });
        this.addEventListener('mouseleave', () => {
          if (this.finePointer.matches) this.setSpread(false);
        });
        // Keyboard focus only — a tap also focuses the link, and that must not skip the tap-to-spread step below.
        this.addEventListener('focusin', (event) => {
          if (event.target.matches(':focus-visible')) this.setSpread(true);
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.setSpread(false);
        });

        // Touch screens have no hover: the first tap fans the stack out, the next tap on a card follows its link.
        this.addEventListener('click', (event) => {
          if (this.finePointer.matches || this.isSpread) return;
          event.preventDefault();
          this.setSpread(true);
        });
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('resize', this.onResize);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
          });
          this.observer.observe(this);
        }

        this.schedule();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearTimeout(this.liftTimer);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
        if (this.observer) this.observer.disconnect();
      }

      render() {
        this.order.forEach((cardIndex, position) => {
          const card = this.cards[cardIndex];
          card.dataset.pos = position;
          card.style.setProperty('--z', 100 - position);
        });
        if (this.isSpread) this.measure();
      }

      // Fan-out offsets: each card sits above the ones in front of it, using real heights since titles can wrap.
      measure() {
        const gap = 8;
        let offset = 0;
        this.order.forEach((cardIndex) => {
          const card = this.cards[cardIndex];
          card.style.setProperty('--spread-y', `${-offset}px`);
          offset += card.offsetHeight + gap;
        });
      }

      setSpread(spread) {
        if (spread === this.isSpread) return;
        this.isSpread = spread;
        if (spread) this.measure();
        this.toggleAttribute('data-spread', spread);
        if (!spread) this.schedule();
      }

      schedule() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.shuffle(), this.delay);
      }

      // Brings the back card to the front: it lifts up above the stack, then drops into the front slot.
      shuffle() {
        if (this.isSpread || document.hidden || !this.inView) {
          this.schedule();
          return;
        }

        const backCard = this.cards[this.order[this.order.length - 1]];
        const moveToFront = () => {
          this.order.unshift(this.order.pop());
          backCard.classList.remove('is-lifting');
          this.render();
          this.schedule();
        };

        if (this.reduceMotion.matches) {
          moveToFront();
          return;
        }

        backCard.classList.add('is-lifting');
        this.liftTimer = setTimeout(moveToFront, 450);
      }

      onDocumentClick(event) {
        if (this.isSpread && !this.finePointer.matches && !this.contains(event.target)) this.setSpread(false);
      }

      onResize() {
        if (this.isSpread) this.measure();
      }
    }
  );
}
